// ─────────────────────────────────────────────────────────────────────
// WHAT A PASTED E-MAIL CODE HAS TO SURVIVE.
//
// Four screens ask for the same six digits — club sign-in, the player door,
// the member door and registration — and all four used to hand the raw value
// straight to a `maxLength={6}` field. That combination is why «din kode er
// 123456», or a plain `123 456` copied out of the message, was refused as
// «wrong or expired»: the browser truncates a paste to fit `maxlength` BEFORE
// React sees it, so `123 456` reached the state as `123 45`, lost its last
// digit, and the person was told their code was bad when the field ate it.
//
// It is the first thing a new player does, so the fix is in one place and the
// four screens share it. `maxLength` is deliberately NOT set on those inputs
// any more — the cap lives here, after the non-digits are gone, which is the
// only order that can keep all six digits of a messy paste.
//
// The same shape already existed for the Banepad pairing code
// (`formatRegistrationCodeInput`). That one is alphanumeric and upper-cased
// because a pairing code is `BANE7K3Q`; these codes are numeric, so this
// strips everything that is not a digit instead. Two code spaces, one rule:
// normalise on the way in, so what is typed and what is compared cannot
// disagree.
// ─────────────────────────────────────────────────────────────────────

/** Digits in an e-mail sign-in code. Mirrors the server's `OTP_LENGTH`. */
export const OTP_CODE_LENGTH = 6;

/**
 * Pure: turn whatever was typed or pasted into the canonical six digits.
 *
 * Drops every non-digit — surrounding words, spaces, non-breaking spaces,
 * dashes, a trailing newline from a copy that took the line as well — and only
 * then applies the length limit. Doing it in the other order is the bug: the
 * cap would spend itself on the rubbish and cut off real digits.
 */
export function formatOtpCodeInput(raw: string): string {
  return String(raw ?? '')
    .replace(/\D/g, '')
    .slice(0, OTP_CODE_LENGTH);
}
